import { useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGlobalMetadata } from '../utils/providers';
import { useOpenRecordInPanel } from '../components/SidePanel/SidePanelContext';
import {
  buildRecordPath,
  parseRecordTarget,
  presentationFor,
  serializeParams,
  withOverlayTarget,
  withRecordTarget,
  withoutRecordTarget,
  type RecordTarget,
} from '../utils/recordTargets';

/**
 * Read the current URL's record target when it names `tableName`.
 *
 * For `page`-presentation tables (funder, rep) the page itself owns the
 * selected record, so it reads the canonical parameter through this hook rather
 * than through its own bespoke query key. Legacy `?funder=`/`?rep=` links are
 * accepted here too, so a shared old link still selects the right record on the
 * render before the resolver rewrites it.
 */
export function useRecordParam(tableName: string): number | null {
  const location = useLocation();
  const target = parseRecordTarget(new URLSearchParams(location.search));
  if (!target || target.tableName !== tableName) return null;
  const id = Number(target.id);
  return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * A record to open. `tableName` and `id` are required; everything else is
 * resolved from metadata when omitted.
 *
 * `table` (the route segment) is accepted so existing `openRecord({...})` call
 * sites keep compiling, but it is only used as an override — the canonical
 * route comes from `routeData.tableToRouteName`.
 */
export interface RecordNavTarget {
  tableName: string;
  id: string | number;
  displayId?: string | number;
  table?: string;
  label?: string;
  /** Presentation sub-state, e.g. the merchant panel's inner tab. */
  tab?: string;
}

export interface RecordNavigator {
  /** Open a record using its registered presentation. */
  openRecord: (target: RecordNavTarget) => void;
  /** Canonical, shareable path for a record — always rooted at its base table route. */
  buildRecordUrl: (target: RecordNavTarget) => string;
  /** Absolute URL for the same, suitable for the clipboard. */
  buildAbsoluteRecordUrl: (target: RecordNavTarget) => string;
  /** Drop the record target from the current URL, preserving unrelated params. */
  closeRecord: () => void;
}

/**
 * The single entry point for record navigation (REG-592).
 *
 * Callers hand over a logical target; the presentation registry in
 * `utils/recordTargets.ts` decides between the persistent side panel, the
 * dismissible modal, and a full-page detail. Nothing here hardcodes a route
 * name — routes come from metadata so renaming a route in the admin UI does not
 * break links.
 */
export function useRecordNavigator(): RecordNavigator {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: metadata } = useGlobalMetadata();
  const openInPanel = useOpenRecordInPanel();

  // RecordLink renders once per grid cell and re-attaches native listeners
  // whenever its opener changes identity. Read the location through a ref so
  // navigating does not invalidate every link in the grid.
  const locationRef = useRef(location);
  locationRef.current = location;

  const routeNameFor = useCallback((target: RecordNavTarget): string => (
    metadata?.routeData?.tableToRouteName?.[target.tableName] || target.table || target.tableName
  ), [metadata?.routeData?.tableToRouteName]);

  const labelFor = useCallback((target: RecordNavTarget): string => {
    if (target.label?.trim()) return target.label;
    const tableLabel = metadata?.tableData?.byName?.[target.tableName]?.ui_label
      || target.tableName.replace(/_/g, ' ');
    return `${tableLabel} #${target.displayId ?? target.id}`;
  }, [metadata?.tableData?.byName]);

  const toRecordTarget = useCallback((target: RecordNavTarget): RecordTarget => ({
    tableName: target.tableName,
    id: String(target.id),
    tab: target.tab,
  }), []);

  const buildRecordUrl = useCallback((target: RecordNavTarget): string => (
    buildRecordPath(routeNameFor(target), toRecordTarget(target))
  ), [routeNameFor, toRecordTarget]);

  const buildAbsoluteRecordUrl = useCallback((target: RecordNavTarget): string => (
    `${window.location.origin}${buildRecordUrl(target)}`
  ), [buildRecordUrl]);

  const openRecord = useCallback((target: RecordNavTarget): void => {
    const presentation = presentationFor(target.tableName);

    // The persistent panel is not addressable from the location bar: it holds
    // several merchants at once, so no single one is "the" current record.
    // Merchants are shared via the tab's Copy link action instead.
    if (presentation === 'panel') {
      openInPanel({
        id: target.id,
        displayId: target.displayId,
        table: routeNameFor(target),
        tableName: target.tableName,
        label: labelFor(target),
      });
      return;
    }

    // A full-page detail has to be on its own route to render at all.
    if (presentation === 'page') {
      navigate(buildRecordUrl(target));
      return;
    }

    // A modal renders over whatever page you are on, so keep the current route
    // and record the target in its query string. The URL stays shareable, and
    // the report/board/grid underneath survives the round trip.
    const { pathname, search: currentSearch } = locationRef.current;
    const params = new URLSearchParams(currentSearch);

    // When the current URL is a full-page detail (funder/rep), its `record`
    // param is what keeps that page open — replacing it would flip the page
    // back to its list view. Carry the modal in the overlay key instead.
    const base = parseRecordTarget(params);
    const search = base && presentationFor(base.tableName) === 'page'
      ? withOverlayTarget(params, toRecordTarget(target))
      : withRecordTarget(params, toRecordTarget(target));
    navigate(`${pathname}?${serializeParams(search)}`);
  }, [buildRecordUrl, labelFor, navigate, openInPanel, routeNameFor, toRecordTarget]);

  const closeRecord = useCallback((): void => {
    const { pathname, search: currentSearch } = locationRef.current;
    const search = serializeParams(withoutRecordTarget(new URLSearchParams(currentSearch)));
    navigate(`${pathname}${search ? `?${search}` : ''}`, { replace: true });
  }, [navigate]);

  return { openRecord, buildRecordUrl, buildAbsoluteRecordUrl, closeRecord };
}

export default useRecordNavigator;
